"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useInactivity = exports.InactivityProvider = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * InactivityProvider - Monitors user activity and enforces automatic logout
 *
 * Matches Angular's behavior:
 * - 20 minutes of inactivity triggers warning
 * - 1 minute warning period before forced logout
 * - Total: 21 minutes until logout
 *
 * Best practices:
 * - Throttled event listeners for performance
 * - Proper cleanup to prevent memory leaks
 * - Accessible warning modal
 * - Clear user communication
 */
const react_1 = require("react");
const AuthProvider_1 = require("./AuthProvider");
// Configuration (matching Angular)
const IDLE_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes
const WARNING_DURATION_MS = 60 * 1000; // 1 minute
const THROTTLE_MS = 2000; // Throttle activity checks to every 2 seconds
// Events to monitor for user activity
const ACTIVITY_EVENTS = [
    'mousedown',
    'mousemove',
    'keydown',
    'scroll',
    'touchstart',
    'click',
];
const InactivityContext = (0, react_1.createContext)(undefined);
const InactivityProvider = ({ children }) => {
    const { isAuthenticated, logout } = (0, AuthProvider_1.useAuth)();
    const [isWarningShown, setIsWarningShown] = (0, react_1.useState)(false);
    const [timeRemaining, setTimeRemaining] = (0, react_1.useState)(WARNING_DURATION_MS / 1000);
    // Refs to track timers and last activity
    const idleTimerRef = (0, react_1.useRef)(null);
    const warningTimerRef = (0, react_1.useRef)(null);
    const countdownIntervalRef = (0, react_1.useRef)(null);
    const lastActivityRef = (0, react_1.useRef)(Date.now());
    const throttleTimeoutRef = (0, react_1.useRef)(null);
    /**
     * Clear all timers
     */
    const clearAllTimers = (0, react_1.useCallback)(() => {
        if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
            idleTimerRef.current = null;
        }
        if (warningTimerRef.current) {
            clearTimeout(warningTimerRef.current);
            warningTimerRef.current = null;
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        if (throttleTimeoutRef.current) {
            clearTimeout(throttleTimeoutRef.current);
            throttleTimeoutRef.current = null;
        }
    }, []);
    /**
     * Force logout due to inactivity
     */
    const forceLogout = (0, react_1.useCallback)(() => {
        clearAllTimers();
        setIsWarningShown(false);
        logout('You were signed out due to inactivity. Please sign in again.');
    }, [clearAllTimers, logout]);
    /**
     * Show warning modal and start countdown to forced logout
     */
    const showWarning = (0, react_1.useCallback)(() => {
        setIsWarningShown(true);
        setTimeRemaining(WARNING_DURATION_MS / 1000);
        // Start countdown
        countdownIntervalRef.current = setInterval(() => {
            setTimeRemaining(prev => {
                const newTime = prev - 1;
                if (newTime <= 0) {
                    return 0;
                }
                return newTime;
            });
        }, 1000);
        // Set timeout for forced logout
        warningTimerRef.current = setTimeout(() => {
            forceLogout();
        }, WARNING_DURATION_MS);
    }, [forceLogout]);
    /**
     * Reset the idle timer (called on user activity)
     */
    const resetTimer = (0, react_1.useCallback)(() => {
        lastActivityRef.current = Date.now();
        // If warning is shown, hide it and reset
        if (isWarningShown) {
            setIsWarningShown(false);
            clearAllTimers();
        }
        // Clear existing idle timer
        if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
        }
        // Start new idle timer
        idleTimerRef.current = setTimeout(() => {
            showWarning();
        }, IDLE_TIMEOUT_MS);
    }, [isWarningShown, clearAllTimers, showWarning]);
    /**
     * Continue session (user clicked "Stay Logged In" in warning modal)
     */
    const continueSession = (0, react_1.useCallback)(() => {
        resetTimer();
    }, [resetTimer]);
    /**
     * Throttled activity handler for performance
     */
    const handleActivity = (0, react_1.useCallback)(() => {
        // Only process activity if authenticated
        if (!isAuthenticated)
            return;
        // Throttle: ignore rapid successive events
        if (throttleTimeoutRef.current)
            return;
        throttleTimeoutRef.current = setTimeout(() => {
            throttleTimeoutRef.current = null;
        }, THROTTLE_MS);
        resetTimer();
    }, [isAuthenticated, resetTimer]);
    /**
     * Set up activity listeners when authenticated
     */
    (0, react_1.useEffect)(() => {
        if (!isAuthenticated) {
            // Not authenticated - clean up everything
            clearAllTimers();
            setIsWarningShown(false);
            return;
        }
        // Start monitoring for inactivity
        resetTimer();
        // Add activity event listeners
        ACTIVITY_EVENTS.forEach(event => {
            window.addEventListener(event, handleActivity, { passive: true });
        });
        // Cleanup on unmount or when authentication changes
        return () => {
            clearAllTimers();
            ACTIVITY_EVENTS.forEach(event => {
                window.removeEventListener(event, handleActivity);
            });
        };
    }, [isAuthenticated, handleActivity, resetTimer, clearAllTimers]);
    /**
     * Check if countdown reached zero and force logout
     */
    (0, react_1.useEffect)(() => {
        if (isWarningShown && timeRemaining <= 0) {
            forceLogout();
        }
    }, [isWarningShown, timeRemaining, forceLogout]);
    const value = {
        isWarningShown,
        timeRemaining,
        resetTimer,
        continueSession,
    };
    return ((0, jsx_runtime_1.jsx)(InactivityContext.Provider, { value: value, children: children }));
};
exports.InactivityProvider = InactivityProvider;
/**
 * Hook to access inactivity context
 */
const useInactivity = () => {
    const context = (0, react_1.useContext)(InactivityContext);
    if (!context) {
        throw new Error('useInactivity must be used within InactivityProvider');
    }
    return context;
};
exports.useInactivity = useInactivity;
